import { useEffect, useMemo, useState } from 'react'
import {
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'
import {
  Activity,
  Copy,
  Edit3,
  Eye,
  KeyRound,
  Plus,
  RefreshCw,
  RotateCcw,
  Send,
  Trash2,
  Webhook,
} from 'lucide-react'
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  LoadMore,
  SegmentedControl,
  SelectField,
  StatusBadge,
  TextField,
  Toggle,
} from '@shared/design-system/ui'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { productsQuery } from '@entities/product/api/product.queries'
import {
  getWebhookDeliveryDetail,
  useWebhookCatalogQuery,
  useWebhookDeliveriesInfiniteQuery,
  useWebhookQuery,
  useWebhooksQuery,
  webhookKeys,
} from '@entities/webhook'
import type {
  CreateWebhookInput,
  ExternalWebhookEventType,
  UpdateWebhookInput,
  WebhookCatalogItem,
  WebhookDeliveriesPage,
  WebhookDeliveryFilters,
  WebhookDeliveryDetail,
  WebhookDeliveryListItem,
  WebhookDeliveryStatus,
  WebhookSubscription,
} from '@entities/webhook'
import {
  testWebhook,
  useCreateWebhookMutation,
  useDeleteWebhookMutation,
  useRedeliverWebhookDeliveryMutation,
  useRotateWebhookSecretMutation,
  useTestWebhookMutation,
  useUpdateWebhookMutation,
} from '../api/webhook.mutations'
import { canRedeliverDelivery, redeliveryConfirmation } from '../lib/delivery-redelivery'
import { webhookUrlError } from '../lib/webhook-url'
import {
  checkboxCard,
  checkboxGrid,
  checkDescription,
  checkText,
  checkTitle,
  codeBlock,
  configGrid,
  deliveryDrawer,
  deliveryList,
  deliveryMain,
  deliveryMeta,
  deliveryRow,
  deliveryType,
  detailHeader,
  detailLead,
  detailPanel,
  detailTitle,
  detailUrl,
  docGrid,
  docStep,
  docSteps,
  drawerActions,
  fieldset,
  form,
  header,
  inlineControls,
  itemActions,
  itemHeader,
  itemMeta,
  itemName,
  itemUrl,
  jsonBlock,
  layout,
  legend,
  listHeader,
  listPanel,
  logItem,
  logList,
  metaBox,
  metaLabel,
  metaValue,
  metric,
  metricLabel,
  metrics,
  metricValue,
  mutedText,
  page,
  panelTitle,
  productGrid,
  productOption,
  secretBox,
  secretValue,
  section,
  sectionHeader,
  sectionTitle,
  statusLine,
  stepNumber,
  subtitle,
  switchLine,
  tabContent,
  title,
  titleGroup,
  toolbar,
  webhookItem,
  webhookList,
  webhookMain,
} from './webhooks.css'

type DetailTab = 'deliveries' | 'config' | 'docs'

type FormState = {
  name: string
  url: string
  eventTypes: ExternalWebhookEventType[]
  productIds: string[]
}

const deliveryStatusOptions: Array<{ label: string; value: 'all' | WebhookDeliveryStatus }> = [
  { label: 'Todas', value: 'all' },
  { label: 'Entregues', value: 'DELIVERED' },
  { label: 'Retry', value: 'RETRYING' },
  { label: 'Falhas', value: 'DEAD' },
]

const signatureSnippet = `import crypto from 'crypto'

export function verifyVendepayWebhook(rawBody, signature, secret) {
  const parts = Object.fromEntries(signature.split(',').map((part) => part.split('=')))
  const timestamp = Number(parts.t)
  const expected = crypto
    .createHmac('sha256', secret)
    .update(\`\${timestamp}.\${rawBody}\`)
    .digest('hex')
  const fresh = Math.abs(Date.now() / 1000 - timestamp) < 300
  return fresh && crypto.timingSafeEqual(Buffer.from(parts.v1), Buffer.from(expected))
}`

const emptyWebhooks: WebhookSubscription[] = []
const emptyCatalog: WebhookCatalogItem[] = []
const emptyProducts: Array<{ id: string; name: string }> = []

export function WebhooksScreen() {
  const webhooksQuery = useWebhooksQuery()
  const queryClient = useQueryClient()
  const catalogQuery = useWebhookCatalogQuery()
  const products = useQuery(productsQuery())
  const [selectedId, setSelectedId] = useState<string>()
  const [tab, setTab] = useState<DetailTab>('deliveries')
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null)
  const [secretReveal, setSecretReveal] = useState<WebhookSubscription | null>(null)
  const [deliveryId, setDeliveryId] = useState<string>()
  const [testEventType, setTestEventType] = useState<ExternalWebhookEventType>('sale.paid')
  const [docEventType, setDocEventType] = useState<ExternalWebhookEventType>('sale.paid')
  const [deliveryStatus, setDeliveryStatus] = useState<'all' | WebhookDeliveryStatus>('all')

  const webhooks = webhooksQuery.data ?? emptyWebhooks
  const catalog = catalogQuery.data ?? emptyCatalog
  const effectiveSelectedId =
    selectedId !== undefined && webhooks.some((item) => item.id === selectedId)
      ? selectedId
      : webhooks[0]?.id
  const selectedQuery = useWebhookQuery(effectiveSelectedId)
  const selected = selectedQuery.data ?? webhooks.find((item) => item.id === effectiveSelectedId)
  const effectiveTestEventType = selected?.eventTypes.includes(testEventType)
    ? testEventType
    : (selected?.eventTypes[0] ?? testEventType)
  const effectiveDocEventType = catalog.some((item) => item.eventType === docEventType)
    ? docEventType
    : (catalog[0]?.eventType ?? docEventType)
  const deliveryFilters = useMemo(
    () => ({
      limit: 10,
      ...(deliveryStatus === 'all' ? {} : { status: [deliveryStatus] }),
    }),
    [deliveryStatus],
  )
  const deliveriesQuery = useWebhookDeliveriesInfiniteQuery(selected?.id, deliveryFilters)
  const deliveries = deliveriesQuery.data?.pages.flatMap((page) => page.items) ?? []
  const deliveryDetailQuery = useQuery({
    queryKey:
      selected && deliveryId
        ? webhookKeys.deliveryDetail(selected.id, deliveryId)
        : webhookKeys.deliveryDetail('__none__', '__none__'),
    queryFn: () => getWebhookDeliveryDetail(selected!.id, deliveryId!),
    enabled: Boolean(selected && deliveryId),
  })

  useEffect(() => {
    if (deliveryDetailQuery.data) {
      syncDeliveryDetailIntoLists(queryClient, deliveryDetailQuery.data)
    }
  }, [deliveryDetailQuery.data, queryClient])

  const createMutation = useCreateWebhookMutation()
  const updateMutation = useUpdateWebhookMutation(selected?.id)
  const deleteMutation = useDeleteWebhookMutation(selected?.id)
  const testMutation = useTestWebhookMutation(selected?.id)
  const redeliverMutation = useRedeliverWebhookDeliveryMutation(selected?.id)
  const rotateMutation = useRotateWebhookSecretMutation(selected?.id)

  const activeCount = webhooks.filter((item) => item.status === 'ACTIVE').length
  const openCount = webhooks.filter((item) => item.circuitState === 'OPEN').length
  const totalEvents = new Set(webhooks.flatMap((item) => item.eventTypes)).size
  const testDeliveries = deliveries.filter((item) => item.isTest).length
  const selectedCatalog =
    catalog.find((item) => item.eventType === effectiveDocEventType) ?? catalog[0]

  async function handleCreate(input: CreateWebhookInput) {
    try {
      const subscription = await createMutation.mutateAsync(input)
      setSelectedId(subscription.id)
      setFormMode(null)
      setSecretReveal(subscription)
      toast.success('Webhook criado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível criar o webhook',
          fallback: 'Confira os dados e tente novamente.',
        }),
      )
    }
  }

  async function handleUpdate(input: UpdateWebhookInput) {
    if (!selected) return
    try {
      await updateMutation.mutateAsync(input)
      setFormMode(null)
      toast.success('Webhook salvo.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível salvar o webhook',
          fallback: 'Confira os dados e tente novamente.',
        }),
      )
    }
  }

  async function handleToggleStatus(checked: boolean) {
    if (!selected) return
    try {
      await updateMutation.mutateAsync({ status: checked ? 'ACTIVE' : 'INACTIVE' })
      toast.success(checked ? 'Webhook ativado.' : 'Webhook pausado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível alterar o status do webhook',
          fallback: 'Tente novamente em instantes.',
        }),
      )
    }
  }

  async function handleDelete() {
    if (!selected) return
    try {
      await deleteMutation.mutateAsync()
      setSelectedId(undefined)
      toast.info('Webhook removido.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível remover o webhook',
          fallback: 'Tente novamente em instantes.',
        }),
      )
    }
  }

  async function handleTest() {
    if (!selected) return
    try {
      await testMutation.mutateAsync(effectiveTestEventType)
      toast.success('Evento de teste enfileirado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível enviar o teste',
          fallback: 'Tente novamente em instantes.',
        }),
      )
    }
  }

  async function handleQuickTest(subscription: WebhookSubscription) {
    const eventType = subscription.eventTypes[0]
    if (!eventType) return
    try {
      await testWebhook({ webhookId: subscription.id, eventType })
      setSelectedId(subscription.id)
      setTab('deliveries')
      void queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries(subscription.id) })
      toast.success('Evento de teste enfileirado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível enviar o teste',
          fallback: 'Tente novamente em instantes.',
        }),
      )
    }
  }

  async function handleRedeliver(delivery: WebhookDeliveryListItem) {
    if (!window.confirm(redeliveryConfirmation(delivery.status))) return

    try {
      await redeliverMutation.mutateAsync(delivery.id)
      toast.success('Reenvio enfileirado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível reenviar a entrega',
          fallback: 'Tente novamente em instantes.',
        }),
      )
    }
  }

  async function handleRotateSecret() {
    if (!selected) return
    try {
      const subscription = await rotateMutation.mutateAsync()
      setSecretReveal(subscription)
      toast.success('Secret rotacionado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível rotacionar o secret',
          fallback: 'Tente novamente em instantes.',
        }),
      )
    }
  }

  return (
    <div className={page} data-testid="webhooks-screen">
      <div className={header}>
        <div className={titleGroup}>
          <h1 className={title}>Webhooks</h1>
          <p className={subtitle}>
            Eventos da conta para automações externas, com assinatura HMAC, histórico de entregas e
            teste pelo mesmo pipeline de produção.
          </p>
        </div>
        <div className={toolbar}>
          <Button leftIcon={<Plus size={16} />} onClick={() => setFormMode('create')}>
            Novo webhook
          </Button>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw size={16} />}
            disabled={webhooksQuery.isFetching}
            onClick={() => void webhooksQuery.refetch()}
          >
            Atualizar
          </Button>
        </div>
      </div>

      <div className={metrics}>
        <div className={metric}>
          <span className={metricLabel}>Ativos</span>
          <span className={metricValue}>{activeCount}</span>
        </div>
        <div className={metric}>
          <span className={metricLabel}>Com circuito aberto</span>
          <span className={metricValue}>{openCount}</span>
        </div>
        <div className={metric}>
          <span className={metricLabel}>Eventos assinados</span>
          <span className={metricValue}>{totalEvents}</span>
        </div>
        <div className={metric}>
          <span className={metricLabel}>Testes na seleção</span>
          <span className={metricValue}>{testDeliveries}</span>
        </div>
      </div>

      <div className={layout}>
        <section className={listPanel}>
          <div className={listHeader}>
            <span className={panelTitle}>Endpoints</span>
            <span className={mutedText}>{webhooks.length} configurado(s)</span>
          </div>
          {webhooks.length === 0 && !webhooksQuery.isLoading ? (
            <EmptyState
              icon={<Webhook size={22} />}
              title="Nenhum webhook"
              description="Crie um endpoint para receber eventos de venda, assinatura e checkout."
              action={
                <Button leftIcon={<Plus size={16} />} onClick={() => setFormMode('create')}>
                  Novo webhook
                </Button>
              }
            />
          ) : (
            <div className={webhookList}>
              {webhooks.map((subscription) => (
                <div
                  key={subscription.id}
                  className={webhookItem({ selected: subscription.id === selected?.id })}
                >
                  <button
                    type="button"
                    className={webhookMain}
                    onClick={() => setSelectedId(subscription.id)}
                  >
                    <div className={itemHeader}>
                      <span>
                        <span className={itemName}>{subscription.name}</span>
                        <span className={itemUrl}>{subscription.url}</span>
                      </span>
                      <SubscriptionStatusBadge subscription={subscription} />
                    </div>
                    <div className={itemMeta}>
                      <StatusBadge
                        tone="informative"
                        size="sm"
                        label={`${subscription.eventTypes.length} evento(s)`}
                      />
                      <StatusBadge
                        tone="neutral"
                        size="sm"
                        label={
                          subscription.productIds?.length
                            ? `${subscription.productIds.length} produto(s)`
                            : 'Todos'
                        }
                      />
                    </div>
                  </button>
                  <div className={itemActions}>
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Send size={14} />}
                      disabled={subscription.status !== 'ACTIVE'}
                      title={
                        subscription.status !== 'ACTIVE'
                          ? 'Ative o webhook antes de enviar um teste.'
                          : undefined
                      }
                      onClick={() => {
                        void handleQuickTest(subscription)
                      }}
                    >
                      Testar
                    </Button>
                    <Button
                      size="sm"
                      variant="text"
                      leftIcon={<Edit3 size={14} />}
                      onClick={() => {
                        setSelectedId(subscription.id)
                        setFormMode('edit')
                      }}
                    >
                      Editar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className={detailPanel}>
          {selected ? (
            <>
              <div className={detailHeader}>
                <div className={detailLead}>
                  <span className={detailTitle}>{selected.name}</span>
                  <span className={detailUrl}>{selected.url}</span>
                  <div className={statusLine}>
                    <SubscriptionStatusBadge subscription={selected} />
                    <StatusBadge
                      tone="neutral"
                      size="sm"
                      label={selected.secret}
                      icon={<KeyRound size={13} />}
                    />
                  </div>
                </div>
                <div className={toolbar}>
                  <Button
                    variant="secondary"
                    leftIcon={<Edit3 size={16} />}
                    onClick={() => setFormMode('edit')}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<RotateCcw size={16} />}
                    disabled={rotateMutation.isPending}
                    onClick={() => void handleRotateSecret()}
                  >
                    Rotacionar
                  </Button>
                </div>
              </div>

              <SegmentedControl<DetailTab>
                value={tab}
                onChange={setTab}
                ariaLabel="Seções do webhook"
                options={[
                  { label: 'Entregas', value: 'deliveries' },
                  { label: 'Configuração', value: 'config' },
                  { label: 'Documentação', value: 'docs' },
                ]}
              />

              {tab === 'deliveries' ? (
                <DeliveriesTab
                  catalog={catalog}
                  selected={selected}
                  deliveries={deliveries}
                  deliveryStatus={deliveryStatus}
                  setDeliveryStatus={setDeliveryStatus}
                  testEventType={effectiveTestEventType}
                  setTestEventType={setTestEventType}
                  testing={testMutation.isPending}
                  onTest={() => void handleTest()}
                  onOpenDelivery={setDeliveryId}
                  onRedeliver={(delivery) => void handleRedeliver(delivery)}
                  redelivering={redeliverMutation.isPending}
                  hasMore={deliveriesQuery.hasNextPage}
                  loadingMore={deliveriesQuery.isFetchingNextPage}
                  fetchMore={() => void deliveriesQuery.fetchNextPage()}
                />
              ) : null}

              {tab === 'config' ? (
                <ConfigTab
                  selected={selected}
                  onToggleStatus={(checked) => void handleToggleStatus(checked)}
                  onEdit={() => setFormMode('edit')}
                  onDelete={() => void handleDelete()}
                  updating={updateMutation.isPending}
                  deleting={deleteMutation.isPending}
                />
              ) : null}

              {tab === 'docs' && selectedCatalog ? (
                <DocumentationTab
                  catalog={catalog}
                  selectedCatalog={selectedCatalog}
                  docEventType={effectiveDocEventType}
                  setDocEventType={setDocEventType}
                />
              ) : null}
            </>
          ) : (
            <EmptyState
              icon={<Activity size={22} />}
              title="Selecione um webhook"
              description="O detalhe mostra entregas, payloads e configuração do endpoint."
            />
          )}
        </section>
      </div>

      <WebhookFormDrawer
        key={formMode === 'edit' ? `edit:${selected?.id ?? 'none'}` : (formMode ?? 'closed')}
        open={formMode !== null}
        mode={formMode ?? 'create'}
        catalog={catalog}
        catalogLoading={catalogQuery.isLoading}
        catalogError={catalogQuery.isError}
        products={products.data ?? emptyProducts}
        subscription={formMode === 'edit' ? selected : undefined}
        loading={createMutation.isPending || updateMutation.isPending}
        onClose={() => setFormMode(null)}
        onCreate={(input) => void handleCreate(input)}
        onUpdate={(input) => void handleUpdate(input)}
      />

      <SecretRevealDrawer subscription={secretReveal} onClose={() => setSecretReveal(null)} />

      <DeliveryDetailDrawer
        delivery={deliveryDetailQuery.data}
        open={deliveryId !== undefined}
        loading={deliveryDetailQuery.isLoading}
        error={deliveryDetailQuery.isError}
        onRetry={() => {
          void deliveryDetailQuery.refetch()
        }}
        onClose={() => setDeliveryId(undefined)}
      />
    </div>
  )
}

function DeliveriesTab(props: {
  catalog: WebhookCatalogItem[]
  selected: WebhookSubscription
  deliveries: WebhookDeliveryListItem[]
  deliveryStatus: 'all' | WebhookDeliveryStatus
  setDeliveryStatus: (value: 'all' | WebhookDeliveryStatus) => void
  testEventType: ExternalWebhookEventType
  setTestEventType: (value: ExternalWebhookEventType) => void
  testing: boolean
  onTest: () => void
  onOpenDelivery: (deliveryId: string) => void
  onRedeliver: (delivery: WebhookDeliveryListItem) => void
  redelivering: boolean
  hasMore: boolean
  loadingMore: boolean
  fetchMore: () => void
}) {
  const enabledEvents = props.catalog.filter((item) =>
    props.selected.eventTypes.includes(item.eventType),
  )
  return (
    <div className={tabContent}>
      <div className={sectionHeader}>
        <span className={sectionTitle}>Eventos de teste</span>
        <div className={inlineControls}>
          <SelectField
            aria-label="Evento de teste"
            value={props.testEventType}
            options={enabledEvents.map((item) => ({ label: item.label, value: item.eventType }))}
            onChange={(event) =>
              props.setTestEventType(event.target.value as ExternalWebhookEventType)
            }
          />
          <Button
            leftIcon={<Send size={16} />}
            disabled={
              props.testing || enabledEvents.length === 0 || props.selected.status !== 'ACTIVE'
            }
            title={
              props.selected.status !== 'ACTIVE'
                ? 'Ative o webhook antes de enviar um teste.'
                : undefined
            }
            onClick={props.onTest}
          >
            Enviar teste
          </Button>
        </div>
      </div>

      <div className={section}>
        <div className={sectionHeader}>
          <span className={sectionTitle}>Histórico</span>
          <SelectField
            aria-label="Status da entrega"
            value={props.deliveryStatus}
            options={deliveryStatusOptions}
            onChange={(event) =>
              props.setDeliveryStatus(event.target.value as 'all' | WebhookDeliveryStatus)
            }
          />
        </div>
        {props.deliveries.length === 0 ? (
          <EmptyState
            icon={<Send size={22} />}
            title="Sem entregas"
            description="As próximas entregas deste endpoint aparecem aqui."
          />
        ) : (
          <>
            <div className={deliveryList}>
              {props.deliveries.map((delivery) => (
                <div key={delivery.id} className={deliveryRow}>
                  <button
                    type="button"
                    className={deliveryMain}
                    onClick={() => props.onOpenDelivery(delivery.id)}
                  >
                    <span className={deliveryType}>
                      {eventTypeLabel(props.catalog, delivery.externalEventType)}
                    </span>
                    <span className={deliveryMeta}>
                      {delivery.deliveryEventId} · {formatDateTime(delivery.createdAt)}
                    </span>
                  </button>
                  <DeliveryStatusBadge delivery={delivery} />
                  <span className={mutedText}>
                    {delivery.attempts}/{delivery.maxAttempts}
                  </span>
                  <div className={itemActions}>
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Eye size={14} />}
                      onClick={() => props.onOpenDelivery(delivery.id)}
                    >
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="text"
                      leftIcon={<RefreshCw size={14} />}
                      disabled={props.redelivering || !canRedeliverDelivery(delivery.status)}
                      onClick={() => props.onRedeliver(delivery)}
                    >
                      Reenviar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <LoadMore
              shown={props.deliveries.length}
              hasMore={props.hasMore}
              loading={props.loadingMore}
              onClick={props.fetchMore}
            />
          </>
        )}
      </div>
    </div>
  )
}

function ConfigTab(props: {
  selected: WebhookSubscription
  updating: boolean
  deleting: boolean
  onToggleStatus: (checked: boolean) => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className={tabContent}>
      <div className={switchLine}>
        <div>
          <span className={sectionTitle}>Status do endpoint</span>
          <div className={mutedText}>
            {props.selected.status === 'ACTIVE' ? 'Recebendo eventos' : 'Pausado'}
          </div>
        </div>
        <Toggle
          checked={props.selected.status === 'ACTIVE'}
          onChange={props.onToggleStatus}
          disabled={props.updating}
          ariaLabel="Alternar status do webhook"
        />
      </div>

      <div className={configGrid}>
        <MetaBox label="Eventos" value={`${props.selected.eventTypes.length} selecionado(s)`} />
        <MetaBox
          label="Produtos"
          value={
            props.selected.productIds?.length
              ? `${props.selected.productIds.length} selecionado(s)`
              : 'Todos'
          }
        />
        <MetaBox label="Circuito" value={circuitLabel(props.selected)} />
        <MetaBox label="Falhas consecutivas" value={String(props.selected.consecutiveFailures)} />
      </div>

      <div className={drawerActions}>
        <Button variant="secondary" leftIcon={<Edit3 size={16} />} onClick={props.onEdit}>
          Editar configuração
        </Button>
        <Button
          variant="danger"
          leftIcon={<Trash2 size={16} />}
          disabled={props.deleting}
          onClick={props.onDelete}
        >
          Remover
        </Button>
      </div>
    </div>
  )
}

function DocumentationTab(props: {
  catalog: WebhookCatalogItem[]
  selectedCatalog: WebhookCatalogItem
  docEventType: ExternalWebhookEventType
  setDocEventType: (value: ExternalWebhookEventType) => void
}) {
  return (
    <div className={tabContent}>
      <div className={sectionHeader}>
        <span className={sectionTitle}>Payload de exemplo</span>
        <SelectField
          aria-label="Evento documentado"
          value={props.docEventType}
          options={props.catalog.map((item) => ({ label: item.label, value: item.eventType }))}
          onChange={(event) =>
            props.setDocEventType(event.target.value as ExternalWebhookEventType)
          }
        />
      </div>
      <div className={docGrid}>
        <div className={docSteps}>
          {[
            'No n8n, crie um Webhook node com método POST e copie a Production URL.',
            'Cole a URL no endpoint da Vendepay e envie um evento de teste.',
            'Use data.buyer.email, data.productId e data.amount para liberar acesso no seu fluxo.',
            'Em produção, responda 2xx rápido e processe o trabalho pesado depois.',
          ].map((text, index) => (
            <div key={text} className={docStep}>
              <span className={stepNumber}>{index + 1}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
        <pre className={jsonBlock}>{JSON.stringify(props.selectedCatalog.example, null, 2)}</pre>
      </div>
      <div className={section}>
        <span className={sectionTitle}>Verificação de assinatura</span>
        <pre className={codeBlock}>{signatureSnippet}</pre>
      </div>
    </div>
  )
}

function WebhookFormDrawer(props: {
  open: boolean
  mode: 'create' | 'edit'
  catalog: WebhookCatalogItem[]
  catalogLoading: boolean
  catalogError: boolean
  products: Array<{ id: string; name: string }>
  subscription?: WebhookSubscription
  loading: boolean
  onClose: () => void
  onCreate: (input: CreateWebhookInput) => void
  onUpdate: (input: UpdateWebhookInput) => void
}) {
  const [state, setState] = useState<FormState>(() => formStateFromSubscription(props.subscription))

  const titleText = props.mode === 'create' ? 'Novo webhook' : 'Editar webhook'
  const catalogUnavailable = props.catalog.length === 0 && !props.catalogLoading
  const urlError = state.url.trim().length > 0 ? webhookUrlError(state.url) : undefined
  const dirty =
    JSON.stringify(state) !== JSON.stringify(formStateFromSubscription(props.subscription))
  const canSubmit =
    state.name.trim().length > 0 &&
    state.url.trim().length > 0 &&
    urlError === undefined &&
    props.catalog.length > 0 &&
    state.eventTypes.length > 0

  function submit() {
    const productIds = state.productIds.length > 0 ? state.productIds : undefined
    if (props.mode === 'create') {
      props.onCreate({
        name: state.name.trim(),
        url: state.url.trim(),
        eventTypes: state.eventTypes,
        ...(productIds !== undefined ? { productIds } : {}),
      })
      return
    }
    props.onUpdate({
      name: state.name.trim(),
      url: state.url.trim(),
      eventTypes: state.eventTypes,
      productIds: state.productIds,
    })
  }

  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      title={titleText}
      description="Defina o endpoint e quais eventos e produtos devem ser enviados."
      size="standard"
      variant="form"
      busy={props.loading}
      dirty={dirty}
      footer={
        <>
          <Button variant="secondary" disabled={props.loading} onClick={props.onClose}>
            Cancelar
          </Button>
          <Button disabled={!canSubmit || props.loading} onClick={submit}>
            {props.mode === 'create' ? 'Criar webhook' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className={form}>
        <TextField
          label="Nome"
          value={state.name}
          onChange={(event) => setState((current) => ({ ...current, name: event.target.value }))}
        />
        <TextField
          label="URL"
          value={state.url}
          placeholder="https://"
          error={urlError}
          helperText="Use HTTPS e um domínio público."
          onChange={(event) => setState((current) => ({ ...current, url: event.target.value }))}
        />

        <fieldset className={fieldset}>
          <legend className={legend}>Eventos</legend>
          {props.catalogLoading ? <span className={mutedText}>Carregando eventos...</span> : null}
          {catalogUnavailable ? (
            <span className={mutedText}>
              {props.catalogError
                ? 'Não foi possível carregar os eventos disponíveis.'
                : 'Nenhum evento disponível para assinatura.'}
            </span>
          ) : null}
          {props.catalog.length > 0 ? (
            <div className={checkboxGrid}>
              {props.catalog.map((item) => {
                const checked = state.eventTypes.includes(item.eventType)
                return (
                  <label key={item.eventType} className={checkboxCard({ checked })}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setState((current) => ({
                          ...current,
                          eventTypes: toggleValue(current.eventTypes, item.eventType),
                        }))
                      }
                    />
                    <span className={checkText}>
                      <span className={checkTitle}>{item.label}</span>
                      <span className={checkDescription}>{item.description}</span>
                    </span>
                  </label>
                )
              })}
            </div>
          ) : null}
        </fieldset>

        <fieldset className={fieldset}>
          <legend className={legend}>Produtos</legend>
          <div className={productGrid}>
            {props.products.map((product) => {
              const checked = state.productIds.includes(product.id)
              return (
                <label key={product.id} className={productOption}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setState((current) => ({
                        ...current,
                        productIds: toggleValue(current.productIds, product.id),
                      }))
                    }
                  />
                  <span>{product.name}</span>
                </label>
              )
            })}
          </div>
          <span className={mutedText}>Sem seleção = todos os produtos.</span>
        </fieldset>
      </div>
    </Drawer>
  )
}

function SecretRevealDrawer(props: {
  subscription: WebhookSubscription | null
  onClose: () => void
}) {
  const secret = props.subscription?.secret
  return (
    <Drawer
      open={props.subscription !== null}
      onClose={props.onClose}
      title="Secret do webhook"
      description="Copie agora. Este valor é exibido uma única vez."
      size="compact"
      variant="technical"
      footer={<Button onClick={props.onClose}>Concluir</Button>}
    >
      <div className={form}>
        <div className={secretBox}>
          <span className={secretValue}>{secret}</span>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Copy size={14} />}
            onClick={() => {
              if (secret) void copyToClipboard(secret, 'Secret copiado.')
            }}
          >
            Copiar
          </Button>
        </div>
        <p className={mutedText}>Guarde este valor agora. Depois ele aparece mascarado.</p>
      </div>
    </Drawer>
  )
}

function DeliveryDetailDrawer(props: {
  delivery: WebhookDeliveryDetail | undefined
  open: boolean
  loading: boolean
  error: boolean
  onRetry: () => void
  onClose: () => void
}) {
  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      title="Entrega"
      description="Payload, tentativas e resposta recebida pelo endpoint."
      size="wide"
      variant="technical"
    >
      <div className={deliveryDrawer}>
        {props.loading ? <span className={mutedText}>Carregando entrega...</span> : null}
        {props.error ? (
          <ErrorState
            title="Não foi possível carregar a entrega."
            description="O histórico continua disponível. Tente buscar este detalhe novamente."
            onRetry={props.onRetry}
          />
        ) : null}
        {props.delivery ? (
          <>
            <div className={statusLine}>
              <DeliveryStatusBadge delivery={props.delivery} />
              <StatusBadge
                tone={props.delivery.isTest ? 'informative' : 'neutral'}
                label={props.delivery.isTest ? 'Teste' : 'Produção'}
              />
            </div>
            <div className={section}>
              <span className={sectionTitle}>Payload</span>
              <pre className={jsonBlock}>{JSON.stringify(props.delivery.payload, null, 2)}</pre>
            </div>
            <div className={section}>
              <span className={sectionTitle}>Tentativas</span>
              <div className={logList}>
                {props.delivery.logs.map((log) => (
                  <div key={log.id} className={logItem}>
                    <span className={metaValue}>#{log.attempt}</span>
                    <span className={mutedText}>{log.httpStatus ?? log.outcome}</span>
                    <span className={mutedText}>
                      {log.error ?? log.responseBody ?? `${log.durationMs}ms`}
                    </span>
                  </div>
                ))}
                {props.delivery.logs.length === 0 ? (
                  <span className={mutedText}>Sem tentativa registrada.</span>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Drawer>
  )
}

function MetaBox({ label, value }: { label: string; value: string }) {
  return (
    <div className={metaBox}>
      <span className={metaLabel}>{label}</span>
      <span className={metaValue}>{value}</span>
    </div>
  )
}

function SubscriptionStatusBadge({ subscription }: { subscription: WebhookSubscription }) {
  if (subscription.status !== 'ACTIVE') {
    return <StatusBadge tone="neutral" size="sm" label="Inativo" />
  }
  if (subscription.circuitState === 'OPEN') {
    return <StatusBadge tone="awaiting" size="sm" label="Circuito aberto" />
  }
  if (subscription.circuitState === 'HALF_OPEN') {
    return <StatusBadge tone="informative" size="sm" label="Sondando" />
  }
  return <StatusBadge tone="success" size="sm" label="Ativo" />
}

function DeliveryStatusBadge({ delivery }: { delivery: WebhookDeliveryListItem }) {
  const tone =
    delivery.status === 'DELIVERED'
      ? 'success'
      : delivery.status === 'DEAD'
        ? 'closed'
        : delivery.status === 'SUPPRESSED'
          ? 'awaiting'
          : delivery.status === 'RETRYING'
            ? 'informative'
            : 'neutral'
  return <StatusBadge tone={tone} size="sm" label={delivery.status} />
}

function syncDeliveryDetailIntoLists(queryClient: QueryClient, delivery: WebhookDeliveryDetail) {
  const listItem = deliveryListItemFromDetail(delivery)
  const queries = queryClient.getQueriesData<InfiniteData<WebhookDeliveriesPage>>({
    queryKey: webhookKeys.deliveries(delivery.subscriptionId),
  })

  for (const [queryKey, current] of queries) {
    if (!current || !Array.isArray(current.pages)) continue

    const filters = deliveryFiltersFromQueryKey(queryKey, delivery.subscriptionId)
    const shouldKeepItem = deliveryMatchesFilters(listItem, filters)
    let changed = false
    const pages = current.pages.map((page) => {
      const currentItem = page.items.find((item) => item.id === listItem.id)
      if (!currentItem || isDeliveryDetailOlderThanListItem(delivery, currentItem)) {
        return page
      }
      changed = true
      return {
        ...page,
        items: shouldKeepItem
          ? page.items.map((item) => (item.id === listItem.id ? listItem : item))
          : page.items.filter((item) => item.id !== listItem.id),
      }
    })

    if (changed) {
      queryClient.setQueryData(queryKey, { ...current, pages })
    }
  }
}

function deliveryListItemFromDetail(delivery: WebhookDeliveryDetail): WebhookDeliveryListItem {
  return {
    id: delivery.id,
    subscriptionId: delivery.subscriptionId,
    sourceEventId: delivery.sourceEventId,
    externalEventType: delivery.externalEventType,
    deliveryEventId: delivery.deliveryEventId,
    status: delivery.status,
    attempts: delivery.attempts,
    maxAttempts: delivery.maxAttempts,
    availableAt: delivery.availableAt,
    ...(delivery.lastHttpStatus !== undefined ? { lastHttpStatus: delivery.lastHttpStatus } : {}),
    ...(delivery.lastError !== undefined ? { lastError: delivery.lastError } : {}),
    isTest: delivery.isTest,
    createdAt: delivery.createdAt,
    updatedAt: delivery.updatedAt,
  }
}

function deliveryFiltersFromQueryKey(
  queryKey: readonly unknown[],
  subscriptionId: string,
): WebhookDeliveryFilters | undefined {
  const deliveryListKey = webhookKeys.deliveries(subscriptionId)
  const filters = queryKey[deliveryListKey.length]
  return isWebhookDeliveryFilters(filters) ? filters : undefined
}

function isWebhookDeliveryFilters(value: unknown): value is WebhookDeliveryFilters {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const candidate = value as { limit?: unknown; status?: unknown }
  return (
    (candidate.limit === undefined || typeof candidate.limit === 'number') &&
    (candidate.status === undefined ||
      (Array.isArray(candidate.status) &&
        candidate.status.every((status) => typeof status === 'string')))
  )
}

function deliveryMatchesFilters(
  delivery: WebhookDeliveryListItem,
  filters: WebhookDeliveryFilters | undefined,
) {
  return !filters?.status?.length || filters.status.includes(delivery.status)
}

function isDeliveryDetailOlderThanListItem(
  delivery: WebhookDeliveryDetail,
  listItem: WebhookDeliveryListItem,
) {
  return Date.parse(delivery.updatedAt) < Date.parse(listItem.updatedAt)
}

function formStateFromSubscription(subscription: WebhookSubscription | undefined): FormState {
  return {
    name: subscription?.name ?? '',
    url: subscription?.url ?? '',
    eventTypes: subscription?.eventTypes ?? ['sale.paid'],
    productIds: subscription?.productIds ?? [],
  }
}

function toggleValue<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

function eventTypeLabel(catalog: WebhookCatalogItem[], eventType: ExternalWebhookEventType) {
  return catalog.find((item) => item.eventType === eventType)?.label ?? eventType
}

function circuitLabel(subscription: WebhookSubscription) {
  if (subscription.circuitState === 'OPEN' && subscription.circuitOpenUntil) {
    return `Aberto até ${formatDateTime(subscription.circuitOpenUntil)}`
  }
  if (subscription.circuitState === 'HALF_OPEN') {
    return 'Sonda em andamento'
  }
  return 'Fechado'
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

async function copyToClipboard(value: string, message: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(message)
  } catch {
    toast.error({
      title: 'Não foi possível copiar',
      description: 'Selecione e copie o valor manualmente.',
    })
  }
}
